import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Button, Grid, Stack, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Loading from "../../../components/Loading";
import ModalComponent from "../../../components/UI/Modal";
import PagesContainer from "../../../components/UI/PagesContainer";
import Row from "../../../components/UI/Row";
import useDate from "../../../hooks/useDate";
import useFetch from "../../../hooks/useFetch";
import {
  getTasySchedMenu,
  getTicketAutomationReceivedRule,
  ITasySchedMenu,
  ITicketAutomationReceivedRule,
  ITicketAutomationReceivedRuleEstablishment,
} from "../../../services/tasySchedMenu.service";
import { notificationSliceActions } from "../../../store/slices/notification";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { ScheduleInsuranceService } from "../../Schedule/Insurance/insurance.service";
import { IInsuranceFavorite } from "../../Schedule/Insurance/model";
import { ScheduleAppointmentService } from "../../Schedule/appointment.service";
import {
  ETasyScheduleTypeCode,
  IScheduleAppointment,
} from "../../Schedule/model";
import Confirm from "../../Schedule/NewSchedule/Steps/Confirm/Confirm";
import TasyOrientations from "../../Schedule/NewSchedule/Steps/Confirm/TasyOrientations";
import Patient from "../../Schedule/NewSchedule/Steps/Patient";
import Schedule from "../../Schedule/NewSchedule/Steps/Schedule/Schedule";
import {
  appointmentSchema,
  TAppointment,
} from "../../Schedule/NewSchedule/schema";
import ConfirmDate from "../../Schedule/NewSchedule/Stepper/ConfirmDate";
import CalendarAddress from "../../Schedule/CalendarAddress";
import FormStepper from "../../Schedule/NewSchedule/Stepper/FormStepper";
import { IStep } from "../../Schedule/NewSchedule/Stepper/models";
import ConsultationBotSchedule from "./ConsultationBotSchedule";
import Establishment from "./Establishment";
import ExamBotSchedule from "./ExamBotSchedule";
import ExamAttachments from "./ExamAttachments";
import Insurance from "./Insurance";
import InsuranceCategoryModal from "./InsuranceCategoryModal";
import SearchAvailability from "./SearchAvailability";

const isGuid = (value?: string) =>
  !!value &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const getInsuranceIntegrationId = (insurance: any) =>
  insurance?.insuranceIntegrationId || insurance?.code || insurance?.id;

const getRuleErrorMessage = (rule?: ITicketAutomationReceivedRule | null) =>
  rule?.errorMessage?.replace(/<[^>]+>/g, " ").trim() || undefined;

const formatInsuranceName = (insurance: any) =>
  insurance?.insuranceName || insurance?.name;

const FEATURED_CONSULTATION_AUTOMATION_CODE = "26";
const RETURN_CONSULTATION_AUTOMATION_CODE = "25";
const CONSULTATION_AUTOMATION_CODE = "20";
const EXAM_AUTOMATION_CODE = "21";
const CHECKUP_AUTOMATION_CODE = "26";

const formatEstablishment = (
  establishment: ITicketAutomationReceivedRuleEstablishment,
) => ({
  id: establishment.establishmentId,
  name: establishment.establishmentName,
  integrationId: establishment.integrationId,
});

interface IRescheduleFlowContext {
  ruleId: string;
  individualId: string;
  individualName: string;
  appointment: IScheduleAppointment & {
    insuranceCategoryCode?: string;
    calendarPreferencesGrouped?: string;
    medicalSpecialtyCode?: number;
  };
}

const NewScheduleFlow = () => {
  const [rule, setRule] = useState<ITicketAutomationReceivedRule | null>(null);

  const [menu, setMenu] = useState<ITasySchedMenu | null>(null);

  const [activeStep, setActiveStep] = useState(0);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const [pendingInsurance, setPendingInsurance] = useState<any | null>(null);

  const [customMessage, setCustomMessage] = useState<string | null>(null);

  const [scheduled, setScheduled] = useState(false);
  const examProceduresEmptyRef = useRef(false);

  const [canSendAttachedFile, setCanSendAttachedFile] = useState(true);

  const { id } = useParams();

  const { pathname, state } = useLocation();

  const navigate = useNavigate();

  const { individual } = useAppSelector((state) => state.user);

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { dayjs } = useDate();

  const rescheduleContext = (
    state as { rescheduleContext?: IRescheduleFlowContext } | null
  )?.rescheduleContext;
  const isRuleFlow = pathname.includes("/ruleFlow/");

  const methods = useForm<TAppointment>({
    resolver: zodResolver(appointmentSchema),
    mode: "onChange",
    defaultValues: {
      search: {
        type: "3",
        date: new Date(),
      },
      patient: {
        id: individual.id,
        individualId: individual.id,
        name: individual.name,
      },
      antecipate: false,
    },
  });

  const { sendRequest: getMenu, loading: menuLoading } =
    useFetch(getTasySchedMenu);

  const { sendRequest: getRule, loading: ruleLoading } = useFetch(
    getTicketAutomationReceivedRule,
  );

  const { sendRequest: validateInsurance } = useFetch(
    ScheduleInsuranceService.newScheduleAllTasy2,
  );

  const { sendRequest: getInsurances } = useFetch(
    ScheduleInsuranceService.list,
  );

  const { sendRequest: createInsuranceFavorite } = useFetch(
    ScheduleInsuranceService.create,
  );

  const { sendRequest: getCalendarRegisterHasTurn } = useFetch(
    ScheduleAppointmentService.getCalendarRegisterHasTurnTasy2,
  );

  const { sendRequest: createScheduleAttachment } = useFetch(
    ScheduleAppointmentService.createScheduleAttachment,
  );
  const { sendRequest: getScheduleAttachmentRules } = useFetch(
    ScheduleAppointmentService.getScheduleAttachmentRules,
  );

  const { sendRequest: scheduleConsultationTasy2 } = useFetch(
    ScheduleAppointmentService.calendarConsultationSchedulingTasy2,
    {
      getErrorMessage: () => getRuleErrorMessage(rule),
    },
  );

  const { sendRequest: scheduleExamTasy2 } = useFetch(
    ScheduleAppointmentService.calendarExamSchedulingTasy2,
    {
      getErrorMessage: () => getRuleErrorMessage(rule),
    },
  );

  const fetchRule = useCallback(async () => {
    if (!id) return;

    if (isRuleFlow) {
      const { data: ruleData, success: ruleSuccess } = await getRule(id);
      if (ruleData && ruleSuccess) {
        setRule(ruleData);
        setMenu({
          id: ruleData.id,
          name: ruleData.name,
          displayOrder: 0,
        });
      }
      return;
    }

    const { data: menu, success } = await getMenu(id);
    if (success && menu) {
      setMenu(menu);
    }

    const ruleId = menu?.ticketAutomationReceivedRule?.id;

    if (!success || !ruleId) return;

    const { data: ruleData, success: ruleSuccess } = await getRule(ruleId);
    if (ruleData && ruleSuccess) {
      setRule(ruleData);
    }
  }, [getMenu, getRule, id, isRuleFlow]);

  useEffect(() => {
    if (!rule || !rescheduleContext) return;

    const { appointment } = rescheduleContext;

    methods.setValue("patient", {
      id: rescheduleContext.individualId,
      individualId: rescheduleContext.individualId,
      name: rescheduleContext.individualName || appointment.pacientName,
    });
    methods.setValue("insurance", {
      id: String(appointment.insuranceCode),
      code: String(appointment.insuranceCode),
      name: appointment.insuranceName,
      insuranceIntegrationId: String(appointment.insuranceCode),
      insuranceName: appointment.insuranceName,
      categoryIntegrationId: appointment.insuranceCategoryCode,
    } as any);

    setActiveStep(
      rule.ticketAutomationCode === RETURN_CONSULTATION_AUTOMATION_CODE ? 1 : 2,
    );
  }, [methods, rescheduleContext, rule]);

  const requiresCategory = useCallback(
    (insuranceIntegrationId?: string) => {
      if (!rule || !insuranceIntegrationId) return false;

      return rule.ticketAutomationReceivedRuleInsuranceCategories.some(
        (category) =>
          category.enable &&
          String(category.insuranceExternalId).trim() ===
            String(insuranceIntegrationId).trim(),
      );
    },
    [rule],
  );

  const ensureInsuranceFavorite = useCallback(
    async (insurance: any) => {
      if (
        isGuid(insurance?.code) ||
        insurance?.code === "1" ||
        insurance?.code === "2"
      ) {
        return true;
      }

      const patient = methods.getValues("patient");
      const insuranceIntegrationId =
        insurance?.insuranceIntegrationId || insurance?.code || insurance?.id;

      const { data, success } = await createInsuranceFavorite({
        cardNumber: insurance?.cardNumber,
        cardValidity: insurance?.cardValidity,
        insuranceIntegrationId: String(insuranceIntegrationId || ""),
        insuranceName: insurance?.insuranceName || insurance?.name,
        categoryIntegrationId: insurance?.categoryIntegrationId,
        categoryName: insurance?.categoryName,
        individualId: patient.individualId || patient.id,
        main: insurance?.main || false,
      });

      if (success && data?.id) {
        methods.setValue("insurance", {
          ...insurance,
          id: data.id,
          code: data.id,
        });
      }

      return success;
    },
    [createInsuranceFavorite, methods],
  );

  const onBeforeNext = useCallback(
    async (activeStep: number) => {
      const hasEstablishmentStep =
        [
          CONSULTATION_AUTOMATION_CODE,
          EXAM_AUTOMATION_CODE,
          CHECKUP_AUTOMATION_CODE,
        ].includes(String(rule?.ticketAutomationCode)) &&
        (rule?.ticketAutomationReceivedRuleEstablishments?.length || 0) > 1;
      const insuranceStep = hasEstablishmentStep ? 2 : 1;

      if (
        rule?.ticketAutomationCode === RETURN_CONSULTATION_AUTOMATION_CODE ||
        activeStep !== insuranceStep
      ) {
        return true;
      }

      const insurance = methods.getValues("insurance") as any;
      const patient = methods.getValues("patient");
      const establishment = methods.getValues("establishment");
      const insuranceIntegrationId = getInsuranceIntegrationId(insurance);

      const { data, success } = await validateInsurance({
        Identifier: Number(insuranceIntegrationId),
        EstablishmentCode: establishment?.integrationId,
      });

      if (!success || !data?.length) {
        dispatch(
          notificationSliceActions.showNotification({
            message: t("page.schedules.newSchedule.insuranceDisabled"),
            type: "error",
          }),
        );
        return false;
      }

      const { data: refreshedInsurances } = await getInsurances({
        IndividualId: patient.individualId || patient.id,
        TicketAutomationReceivedRuleId: rule?.id,
      });

      const refreshedInsurance = refreshedInsurances?.find(
        (item: IInsuranceFavorite) =>
          item.code === insurance.code ||
          String(item.insuranceIntegrationId).trim() ===
            String(insuranceIntegrationId).trim(),
      );

      const selectedInsurance = refreshedInsurance
        ? {
            ...refreshedInsurance,
            id: refreshedInsurance.code,
            name: formatInsuranceName(refreshedInsurance),
            code: refreshedInsurance.code,
          }
        : insurance;

      if (
        requiresCategory(String(insuranceIntegrationId)) &&
        !selectedInsurance?.categoryIntegrationId
      ) {
        setPendingInsurance(selectedInsurance);
        setCategoryModalOpen(true);
        return false;
      }

      if (refreshedInsurance) {
        methods.setValue("insurance", selectedInsurance);
      }

      return await ensureInsuranceFavorite(selectedInsurance);
    },
    [
      dispatch,
      ensureInsuranceFavorite,
      getInsurances,
      methods,
      requiresCategory,
      rule?.id,
      rule?.ticketAutomationCode,
      rule?.ticketAutomationReceivedRuleEstablishments?.length,
      t,
      validateInsurance,
    ],
  );

  const onCategorySuccess = useCallback(
    (insurance: any) => {
      methods.setValue("insurance", insurance);
      setCategoryModalOpen(false);
      setPendingInsurance(null);
      const hasEstablishmentStep =
        [
          CONSULTATION_AUTOMATION_CODE,
          EXAM_AUTOMATION_CODE,
          CHECKUP_AUTOMATION_CODE,
        ].includes(String(rule?.ticketAutomationCode)) &&
        (rule?.ticketAutomationReceivedRuleEstablishments?.length || 0) > 1;
      setActiveStep(hasEstablishmentStep ? 3 : 2);
    },
    [
      methods,
      rule?.ticketAutomationCode,
      rule?.ticketAutomationReceivedRuleEstablishments?.length,
    ],
  );

  const getCustomMessageForConsultation = useCallback(async () => {
    const search = methods.getValues("search");
    if (
      rule?.ticketAutomationCode !== CONSULTATION_AUTOMATION_CODE ||
      search.type !== ETasyScheduleTypeCode.CONSULTATION
    ) {
      return null;
    }

    const professionalId = search.professional?.id;
    if (!professionalId) return null;

    const customMessageRecord =
      rule.ticketAutomationReceivedCustomMessages?.find(
        (message) =>
          !message.ticketAutomationReceivedCustomMessageCalendars?.length ||
          message.ticketAutomationReceivedCustomMessageCalendars.some(
            (calendar) =>
              String(calendar.externalId) === String(professionalId),
          ),
      );

    if (!customMessageRecord?.message) return null;

    const establishment = methods.getValues("establishment");
    const { data, success } = await getCalendarRegisterHasTurn({
      MedicalSpecialtyCode: Number(search.speciality.id),
      Identifier: Number(professionalId),
      EstablishmentCode: establishment?.integrationId,
    });

    if (success && !data?.length) {
      return customMessageRecord.message;
    }

    return null;
  }, [getCalendarRegisterHasTurn, methods, rule]);

  const getExamCustomMessageByLocation = useCallback(
    (locationId?: string | number | null) => {
      if (!locationId) return null;

      return (
        rule?.ticketAutomationReceivedCustomMessages?.find((message) =>
          message.ticketAutomationReceivedCaleExamGroupings?.some(
            (grouping) => String(grouping.externalId) === String(locationId),
          ),
        )?.message || null
      );
    },
    [rule],
  );

  const getCustomMessageForExam = useCallback(() => {
    const search = methods.getValues("search") as any;
    if (
      rule?.ticketAutomationCode !== EXAM_AUTOMATION_CODE ||
      search.type !== ETasyScheduleTypeCode.EXAM
    ) {
      return null;
    }

    return getExamCustomMessageByLocation(search.location?.id);
  }, [getExamCustomMessageByLocation, methods, rule?.ticketAutomationCode]);

  const getCustomMessageBeforeSchedule = useCallback(async () => {
    return (
      getCustomMessageForExam() || (await getCustomMessageForConsultation())
    );
  }, [getCustomMessageForConsultation, getCustomMessageForExam]);

  const onFinish = async () => {
    if (!rule?.id) return;

    const values = methods.getValues() as any;
    const insuranceCode = Number(getInsuranceIntegrationId(values.insurance));
    const establishmentCode = values.establishment?.integrationId;
    const isExam = rule.ticketAutomationCode === EXAM_AUTOMATION_CODE;

    const response = isExam
      ? await scheduleExamTasy2({
          identifier: values.schedule.identifier,
          individualId: values.patient.individualId || values.patient.id,
          ticketAutomationReceivedRuleId: rule.id,
          insuranceCode,
          insuranceCategoryCode: values.insurance?.categoryIntegrationId,
          establishmentCode,
          internalProcedureCode: Number(
            values.search.examGroup?.procedureInternalCode ??
              values.schedule.procedureInternalCode,
          ),
          minutesDuration: Number(values.schedule.minutesDuration),
          scheduleAttachmentIds: values.scheduleAttachments?.id
            ? [values.scheduleAttachments.id]
            : undefined,
        })
      : await scheduleConsultationTasy2({
          identifier: values.schedule.identifier,
          individualId: values.patient.individualId || values.patient.id,
          ticketAutomationReceivedRuleId: rule.id,
          insuranceCode,
          insuranceCategoryCode: values.insurance?.categoryIntegrationId,
          establishmentCode,
        });

    if (response.success) {
      setScheduled(true);
    }
  };

  const saveExamAttachments = useCallback(async () => {
    if (rule?.ticketAutomationCode !== EXAM_AUTOMATION_CODE) return true;

    const values = methods.getValues();
    const { data: rules } = await getScheduleAttachmentRules({
      ticketAutomationReceivedRuleId: rule.id,
      calendarIdentifier: values.schedule?.calendarIdentifier ?? undefined,
      insuranceCode:
        values.schedule?.insuranceCode ||
        Number(getInsuranceIntegrationId(values.insurance)),
      procedureInternalCode:
        values.schedule?.procedureInternalCode ?? undefined,
      calendarPreferencesGrouped:
        values.schedule?.calendarPreferencesGrouped ?? undefined,
    });
    const filesByRule = values.scheduleAttachments?.files || {};
    const files = Object.entries(filesByRule)
      .filter(([, file]: any) => file?.id)
      .map(([ruleId, file]: any) => ({
        ticketAutomationReceivedConfFileId: ruleId,
        mediaFileId: file.id,
      }));

    const missingRule = rules?.find(
      (rule) =>
        !files.some(
          (file) => file.ticketAutomationReceivedConfFileId === rule.id,
        ),
    );
    if (missingRule) {
      dispatch(
        notificationSliceActions.showNotification({
          message: t("page.schedules.newSchedule.attachments.required", {
            file: missingRule.message.replace(/<[^>]+>/g, " "),
          }),
          type: "error",
        }),
      );
      return false;
    }

    if (!files.length) return true;

    const deleteMediaFilesIds = values.scheduleAttachments?.deletedFilesIds;

    const { data, success } = await createScheduleAttachment({
      calendarExamIdentifier: String(values.schedule.identifier),
      mediaFilesIds: files.map(({ mediaFileId }) => mediaFileId),
      establishmentId: values.establishment?.id,
      deleteMediaFilesIds: deleteMediaFilesIds ?? undefined,
    });

    if (success && data?.id) {
      methods.setValue("scheduleAttachments.deletedFilesIds", null);
      methods.setValue("scheduleAttachments.id" as any, data.id);
    }

    return success;
  }, [
    createScheduleAttachment,
    dispatch,
    getScheduleAttachmentRules,
    methods,
    rule,
    t,
  ]);

  const steps: IStep<TAppointment>[] = useMemo(() => {
    const useConsultationBot =
      !!rule?.ticketAutomationCode &&
      rule.ticketAutomationCode !== EXAM_AUTOMATION_CODE;
    const useExamBot = rule?.ticketAutomationCode === EXAM_AUTOMATION_CODE;
    const useInsuranceStep =
      rule?.ticketAutomationCode !== RETURN_CONSULTATION_AUTOMATION_CODE;
    const useEstablishmentStep =
      [
        CONSULTATION_AUTOMATION_CODE,
        EXAM_AUTOMATION_CODE,
        CHECKUP_AUTOMATION_CODE,
      ].includes(String(rule?.ticketAutomationCode)) &&
      (rule?.ticketAutomationReceivedRuleEstablishments?.length || 0) > 1;

    const nextSteps: IStep<TAppointment>[] = [
      {
        label: "page.schedules.newSchedule.steps.patient.title",
        element: <Patient />,
        fields: ["patient"],
      },
    ];

    if (useEstablishmentStep) {
      nextSteps.push({
        label: "page.schedules.newSchedule.steps.establishment.title",
        element: <Establishment rule={rule} />,
        fields: ["establishment"],
      });
    }

    if (useInsuranceStep) {
      nextSteps.push({
        label: "page.schedules.newSchedule.steps.insurance.title",
        element: <Insurance rule={rule} />,
        fields: ["insurance"],
      });
    }

    nextSteps.push(
      {
        label: "page.schedules.newSchedule.steps.search.title",
        element: (
          <SearchAvailability
            rule={rule}
            getExamCustomMessage={getExamCustomMessageByLocation}
            onShowCustomMessage={setCustomMessage}
            onExamProceduresLoaded={(count) => {
              examProceduresEmptyRef.current = count === 0;
            }}
          />
        ),
        fields:
          rule?.ticketAutomationCode === FEATURED_CONSULTATION_AUTOMATION_CODE
            ? []
            : ["search"],
      },
      {
        label: "page.schedules.newSchedule.steps.schedule.title",
        element: useExamBot ? (
          <ExamBotSchedule rule={rule} />
        ) : useConsultationBot ? (
          <ConsultationBotSchedule rule={rule} menuName={menu?.name} />
        ) : (
          <Schedule />
        ),
        fields: ["schedule"],
      },
    );

    if (useExamBot) {
      nextSteps.push({
        label: "page.schedules.newSchedule.steps.attachments",
        element: (
          <ExamAttachments rule={rule} onFileSelect={setCanSendAttachedFile} />
        ),
        fields: [],
      });
    }

    nextSteps.push({
      label: "page.schedules.newSchedule.steps.confirm",
      element: <Confirm rule={rule} useNewScheduleInsuranceRegister />,
      fields: [],
    });

    return nextSteps;
  }, [getExamCustomMessageByLocation, menu?.name, rule]);

  const getSearchStep = useCallback(() => {
    const hasInsuranceStep =
      rule?.ticketAutomationCode !== RETURN_CONSULTATION_AUTOMATION_CODE;
    const hasEstablishmentStep =
      [
        CONSULTATION_AUTOMATION_CODE,
        EXAM_AUTOMATION_CODE,
        CHECKUP_AUTOMATION_CODE,
      ].includes(String(rule?.ticketAutomationCode)) &&
      (rule?.ticketAutomationReceivedRuleEstablishments?.length || 0) > 1;
    return (hasEstablishmentStep ? 1 : 0) + (hasInsuranceStep ? 2 : 1);
  }, [
    rule?.ticketAutomationCode,
    rule?.ticketAutomationReceivedRuleEstablishments?.length,
  ]);

  // Sem procedimentos para o tipo de exame o campo obrigatório barraria o
  // "Próximo" antes da mensagem; por isso a checagem roda antes da validação.
  const onBeforeValidateWithCustomMessage = useCallback(
    (activeStep: number) => {
      if (
        activeStep !== getSearchStep() ||
        rule?.ticketAutomationCode !== EXAM_AUTOMATION_CODE ||
        !examProceduresEmptyRef.current
      ) {
        return true;
      }

      const message = getCustomMessageForExam();
      if (!message) return true;

      setCustomMessage(message);
      return false;
    },
    [getCustomMessageForExam, getSearchStep, rule?.ticketAutomationCode],
  );

  const onBeforeNextWithCustomMessage = useCallback(
    async (activeStep: number) => {
      const searchStep = getSearchStep();
      const attachmentStep =
        rule?.ticketAutomationCode === EXAM_AUTOMATION_CODE
          ? searchStep + 2
          : -1;

      if (activeStep === searchStep) {
        const message = await getCustomMessageBeforeSchedule();
        if (message) {
          setCustomMessage(message);
          return false;
        }
      }

      if (canSendAttachedFile && activeStep === attachmentStep) {
        const success = await saveExamAttachments();
        if (success) {
          setCanSendAttachedFile(false);
        }

        return success;
      }

      return await onBeforeNext(activeStep);
    },
    [
      rule?.ticketAutomationCode,
      canSendAttachedFile,
      getSearchStep,
      getCustomMessageBeforeSchedule,
      onBeforeNext,
      saveExamAttachments,
    ],
  );

  useEffect(() => {
    fetchRule();
  }, [fetchRule]);

  useEffect(() => {
    const establishments =
      rule?.ticketAutomationReceivedRuleEstablishments || [];
    if (establishments.length === 1) {
      methods.setValue("establishment", formatEstablishment(establishments[0]));
    }
  }, [methods, rule?.ticketAutomationReceivedRuleEstablishments]);

  if (menuLoading || ruleLoading) {
    return <Loading />;
  }

  if (scheduled) {
    const values = methods.getValues();

    return (
      <PagesContainer redirectTo="/schedule-new/menus">
        <FormProvider<TAppointment> {...methods}>
          <Stack gap={3} sx={{ px: 2 }}>
            <Alert severity="success">
              {t("page.schedules.newSchedule.success")}
            </Alert>
            <Grid container gap={3} flexDirection="row">
              <Stack spacing={1}>
                <Row
                  label={t(
                    values.search?.type === "2"
                      ? "page.schedules.newSchedule.search.procedure"
                      : "page.schedules.newSchedule.confirm.descriptionSite",
                  )}
                  value={values.schedule.descriptionSite}
                  compact
                />
                {values.search?.type !== "2" &&
                  values.search?.speciality?.name && (
                    <Row
                      label={t("page.schedules.newSchedule.search.speciality")}
                      value={values.search.speciality.name}
                      compact
                    />
                  )}
                <Row
                  label={t("page.schedules.newSchedule.confirm.patient")}
                  value={values.patient.name}
                  compact
                />
                <Row
                  label={t("page.schedules.newSchedule.confirm.insurance")}
                  value={values.insurance.name}
                  compact
                />
                <CalendarAddress
                  address={values.schedule.calendarAddress}
                  compact
                  showMapButton
                />
              </Stack>
              <Grid item xs={true}>
                <ConfirmDate
                  calendarStatusCode={null}
                  duration={values.schedule.minutesDuration}
                  startHour={dayjs(values.schedule.calendarStartDate)}
                  scheduleDate={values.schedule.calendarStartDate}
                />
              </Grid>
            </Grid>
            <TasyOrientations
              rule={rule}
              moment="finished"
              useNewScheduleInsuranceRegister
            />
            <Box>
              <Button
                variant="contained"
                onClick={() => navigate("/schedule-new")}
              >
                {t("page.schedules.mySchedules.title")}
              </Button>
            </Box>
          </Stack>
        </FormProvider>
      </PagesContainer>
    );
  }

  const patient = methods.getValues("patient");

  return (
    <PagesContainer redirectTo="/schedule-new/menus">
      <FormProvider<TAppointment> {...methods}>
        <InsuranceCategoryModal
          open={categoryModalOpen}
          insurance={pendingInsurance}
          patient={patient}
          rule={rule}
          establishmentCode={methods.getValues("establishment")?.integrationId}
          onClose={() => setCategoryModalOpen(false)}
          onSuccess={onCategorySuccess}
        />
        <ModalComponent
          open={!!customMessage}
          onClose={() => setCustomMessage(null)}
          dialogMaxWidth="sm"
        >
          <Box sx={{ minWidth: 320, maxWidth: 560 }}>
            <Typography
              component="div"
              sx={{ whiteSpace: "pre-line" }}
              dangerouslySetInnerHTML={{ __html: customMessage || "" }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => setCustomMessage(null)}
              >
                OK
              </Button>
            </Box>
          </Box>
        </ModalComponent>
        <FormStepper<TAppointment>
          steps={steps}
          error={undefined}
          activeStep={activeStep}
          onActiveStepChange={setActiveStep}
          finishLabel={t("page.schedules.newSchedule.sched")!}
          onBeforeValidate={onBeforeValidateWithCustomMessage}
          onBeforeNext={onBeforeNextWithCustomMessage}
          onFinish={onFinish}
        />
      </FormProvider>
    </PagesContainer>
  );
};

export default NewScheduleFlow;
